/**
 * Lightweight form-field wrappers for D5 create/edit forms.
 *
 * Built directly on React Hook Form register + shadcn `ui` primitives (Input,
 * Textarea, Label) so the D5 forms compile against pinned deps without coupling
 * to the un-pinned D2 `refine-ui` form wrappers. Validation is enforced by the
 * Zod resolver wired in each form (shared `create*Schema`/`update*Schema`).
 */
import type { ReactNode } from "react";
import type {
  FieldError,
  FieldValues,
  Path,
  UseFormRegister,
} from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

function ErrorText({ error }: { error?: FieldError }) {
  if (!error?.message) return null;
  return <p className="text-xs text-destructive">{error.message}</p>;
}

/**
 * Coerce a numeric input's string value to a number, mapping empty/invalid to
 * `undefined`. Registering optional number fields with this (instead of RHF's
 * `valueAsNumber`) keeps an empty field as `undefined` rather than `NaN`, which
 * the optional Zod number schemas accept — otherwise an empty optional number
 * silently blocks form submission. Mirrors `key-results` create/edit.
 */
function numberOrUndefined(v: string): number | undefined {
  if (v === "" || v == null) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function FieldShell({
  label,
  htmlFor,
  required,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  required?: boolean;
  error?: FieldError;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor}>
        {label}
        {required ? <span className="ml-0.5 text-destructive">*</span> : null}
      </Label>
      {children}
      <ErrorText error={error} />
    </div>
  );
}

export function TextField<TValues extends FieldValues>({
  name,
  label,
  register,
  error,
  required,
  type = "text",
  placeholder,
}: {
  name: Path<TValues>;
  label: string;
  register: UseFormRegister<TValues>;
  error?: FieldError;
  required?: boolean;
  type?: "text" | "number";
  placeholder?: string;
}) {
  return (
    <FieldShell label={label} htmlFor={name} required={required} error={error}>
      <Input
        id={name}
        type={type}
        placeholder={placeholder}
        {...register(
          name,
          type === "number" ? { setValueAs: numberOrUndefined } : undefined,
        )}
      />
    </FieldShell>
  );
}

export function TextAreaField<TValues extends FieldValues>({
  name,
  label,
  register,
  error,
  required,
  placeholder,
  rows = 4,
}: {
  name: Path<TValues>;
  label: string;
  register: UseFormRegister<TValues>;
  error?: FieldError;
  required?: boolean;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <FieldShell label={label} htmlFor={name} required={required} error={error}>
      <Textarea id={name} rows={rows} placeholder={placeholder} {...register(name)} />
    </FieldShell>
  );
}

export function SelectField<TValues extends FieldValues>({
  name,
  label,
  register,
  error,
  required,
  options,
  includeEmpty = true,
}: {
  name: Path<TValues>;
  label: string;
  register: UseFormRegister<TValues>;
  error?: FieldError;
  required?: boolean;
  options: readonly string[];
  includeEmpty?: boolean;
}) {
  return (
    <FieldShell label={label} htmlFor={name} required={required} error={error}>
      <select
        id={name}
        className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        {...register(name, { setValueAs: (v: string) => (v === "" ? undefined : v) })}
      >
        {includeEmpty ? <option value="">—</option> : null}
        {options.map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}
